import { Button, Checkbox as CheckboxAntd, Collapse, Form } from 'antd'
import { CollapseProps, FormInstance } from 'antd/lib'
import { useContext } from 'react'

import Loading from '@/components/Loading'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import { TeamDetails } from '@/types'
import FormAntd from '@/ui/form/antd/FormAntd'
import cn from '@/utils/tailwind/cn'
import { ZentakeContext } from '@/utils/ZentakeContext'

import { roleList } from '../../permissionList'
import { countAllRolePermissions } from '../../utils'
import { RoleFormModel, useRoleForm } from './useRoleForm'

type Props = {
  initialValues?: RoleFormModel
  onFormSubmit: (
    data: RoleFormModel,
    form?: FormInstance<RoleFormModel>
  ) => void
  isPending: boolean
}

const RoleForm = ({ initialValues, onFormSubmit, isPending }: Props) => {
  const { form, rule } = useRoleForm()
  const zentakeContext = useContext(ZentakeContext)
  const updateParams = useUpdateURLSearchParams()
  const onCancel = () =>
    updateParams(['addNew', undefined], ['editId', undefined])
  const teamDetails = zentakeContext?.teamDetails || ({} as TeamDetails)

  const ROLE_LIST = roleList(teamDetails.assigned_submission_owner)

  const hasInitialValues = Object.keys(initialValues || {}).length

  let buttonSubmitText = 'Create'
  if (hasInitialValues) buttonSubmitText = 'Save'

  const handleSelectAllInSection = (key: string, isChecked: boolean) => {
    const role = isChecked && ROLE_LIST?.find((role) => role.key === key)
    const permissions = role
      ? role.permissions.map((permission) => permission.value)
      : []

    form.setFieldValue(key as keyof RoleFormModel, permissions)
  }

  const handleSelectAllPermissions = (isChecked: boolean) => {
    const permissions: { [key: string]: string[] | undefined } = {}

    ROLE_LIST.forEach((role) => {
      if (isChecked) {
        permissions[role.key] = role.permissions.map(
          (permission) => permission.value
        )
      } else {
        permissions[role.key] = undefined
      }
    })
    form.setFieldsValue(permissions)
  }

  const countTotalRolePermissions = countAllRolePermissions(ROLE_LIST)

  const items: CollapseProps['items'] = ROLE_LIST.map(
    ({ title, permissions, key }, index) => {
      const keyValue = (key as keyof RoleFormModel) || 'permissions'
      return {
        key: index,
        className:
          'py-3 [&_.ant-collapse-header]:!px-0 [&_.ant-collapse-content-box]:!px-0 [&_.ant-collapse-header:hover]:!px-2 [&_.ant-collapse-header:hover]:text-bg-primary [&_.ant-collapse-header:hover]:!bg-transparent [&_.ant-collapse-header:hover]:text-primary [&_.ant-collapse-header]:font-semibold [&_.ant-collapse-header]:text-base',
        label: (
          <div className="flex">
            <span className="w-full">{title}</span>
            <Form.Item<RoleFormModel>
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues?.[keyValue] !== currentValues?.[keyValue]
              }
            >
              {({ getFieldValue }) => {
                const getKeyValue = (getFieldValue(keyValue) as string[]) || []
                const totalChecked = getKeyValue.length
                const totalPermissionByKey =
                  ROLE_LIST?.find((item) => item.key === keyValue)?.permissions
                    .length || 0
                const isCheckedAll = totalPermissionByKey === totalChecked
                const isPartiallyChecked = totalChecked > 1 && !isCheckedAll

                return (
                  <FormAntd.Checkbox
                    checked={isCheckedAll}
                    className={cn(
                      'mr-1 flex-row-reverse items-center whitespace-nowrap font-normal text-gray-400 [&>.ant-checkbox]:scale-125',
                      isCheckedAll && 'text-primary'
                    )}
                    indeterminate={Boolean(totalChecked) && !isCheckedAll}
                    onChange={(e) =>
                      handleSelectAllInSection(keyValue, e.target.checked)
                    }
                    onClick={(e) => e?.stopPropagation()}
                  >
                    {totalChecked === 1 ? '1 permission selected' : null}
                    {isPartiallyChecked
                      ? `${totalChecked} permissions selected`
                      : null}
                    {isCheckedAll ? 'All selected' : null}
                    {!totalChecked ? 'None selected' : null}
                  </FormAntd.Checkbox>
                )
              }}
            </Form.Item>
          </div>
        ),
        children: (
          <Form.Item<RoleFormModel> className="w-full py-0.5" name={keyValue}>
            <CheckboxAntd.Group className="w-full">
              {permissions.map((subItem) => (
                <FormAntd.Checkbox
                  className="w-full flex-row-reverse items-center justify-between whitespace-nowrap py-2 font-normal text-gray-400 after:hidden [&>.ant-checkbox]:scale-125 [&_.ant-checkbox]:mr-1"
                  key={subItem.value}
                  value={subItem.value}
                >
                  <div className="flex w-full flex-col">
                    <strong className="text-sm font-semibold text-gray-500">
                      {subItem.title}
                    </strong>
                    <p className="text-sm text-gray-500">{subItem.subtitle}</p>
                  </div>
                </FormAntd.Checkbox>
              ))}
            </CheckboxAntd.Group>
          </Form.Item>
        ),
        forceRender: true,
      }
    }
  )

  if (isPending) return <Loading className="!h-60" />

  return (
    <FormAntd.Root<RoleFormModel>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      onFinish={(data) => onFormSubmit?.(data, form)}
      validateTrigger={['onBlur']}
    >
      <Form.Item<RoleFormModel>
        className="mt-8 w-full"
        label="Role Name"
        name="name"
        rules={[rule]}
      >
        <FormAntd.Input type="text" />
      </Form.Item>

      <div className="mt-4 flex w-full border-b border-gray-300 py-2">
        <p className="w-full text-lg font-medium text-gray-800">Permissions</p>
        <Form.Item<RoleFormModel>
          noStyle
          shouldUpdate={(prevValues, currentValues) => {
            const keysToCheck: (keyof RoleFormModel)[] = [
              'client_profile_permissions',
              'form_packet_submission_permissions',
              'packets_forms_building_permissions',
              'report_data',
              'settings',
            ]

            return keysToCheck.some(
              (key) => prevValues?.[key] !== currentValues?.[key]
            )
          }}
        >
          {({ getFieldValue }) => {
            const keys = [
              'client_profile_permissions',
              'form_packet_submission_permissions',
              'packets_forms_building_permissions',
              'report_data',
              'settings',
            ]

            let countAllChecked = 0

            keys.forEach((key) => {
              const checkedCount = (
                (getFieldValue(key as keyof RoleFormModel) as string[]) || []
              ).length
              countAllChecked += checkedCount
            })

            const isCheckedAll = countAllChecked === countTotalRolePermissions

            return (
              <FormAntd.Checkbox
                checked={isCheckedAll}
                className={cn(
                  'mr-1 flex-row-reverse items-center whitespace-nowrap text-gray-400 [&>.ant-checkbox]:scale-125',
                  isCheckedAll && 'text-primary'
                )}
                indeterminate={Boolean(countAllChecked) && !isCheckedAll}
                onChange={(e) => handleSelectAllPermissions(e.target.checked)}
              >
                {isCheckedAll ? 'All selected' : 'Select all'}
              </FormAntd.Checkbox>
            )
          }}
        </Form.Item>
      </div>

      <Collapse
        accordion
        bordered={false}
        className="mt-6"
        defaultActiveKey={['0']}
        items={items}
        style={{
          background: 'transparent',
        }}
      />
      <div className="-mb-2 -ml-6 flex w-[calc(100%+48px)] justify-end gap-4 border-t border-gray-200 pt-4">
        <Button onClick={onCancel} type="default">
          Cancel
        </Button>
        <Button className="mr-6" htmlType="submit" type="primary">
          {buttonSubmitText}
        </Button>
      </div>
    </FormAntd.Root>
  )
}

export default RoleForm
